import { memo, useState, useCallback } from "react"
import { Link } from "react-router-dom"
import type { PortfolioItem, Artist } from "@/types/models"
import { CraveButton } from "@/components/fotw/CraveButton"
import { formatArtistDisplayName } from "@/lib/utils"

interface FotwCardProps {
  item: PortfolioItem
  artist?: Artist
  weekIndex: number
  onImageLoad?: () => void
  votingEnded?: boolean
}

export const FotwCard = memo(function FotwCard({ item, artist, weekIndex, onImageLoad, votingEnded }: FotwCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false)

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true)
    onImageLoad?.()
  }, [onImageLoad])

  return (
    <div className="flex flex-col gap-2">
      {/* Image Container with Button inside but extra height */}
      <div className="relative w-full group">
        <Link to={`/fotw/${item.id}`}>
          <div className="cursor-pointer relative pb-[60px]">
            {/* Skeleton placeholder */}
            {!imageLoaded && (
              <div className="w-full aspect-square bg-muted animate-pulse rounded-[var(--card-radius)] shadow-lg" />
            )}
            
            <img
              src={item.compressedImageUrl || item.imageUrl}
              alt={item.caption || "Flash tattoo design"}
              className={`w-full h-auto object-cover rounded-[var(--card-radius)] block transition-opacity duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              loading="lazy"
              onLoad={handleImageLoad}
            />
            {/* Primary color overlay */}
            <div className="absolute inset-0 bg-primary/5 rounded-[var(--card-radius)]" />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-[var(--card-radius)]" />
            
            {/* Size pill - Top Left */}
            {item.minSize && (
              <div className="absolute top-2 left-2 bg-background text-foreground text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                <span>~{item.minSize}cm</span>
              </div>
            )}
            
            {/* Price pill - Top Right */}
            {item.minPrice && (
              <div className="absolute top-2 right-2 bg-background text-foreground text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                <span>{item.minPrice}€</span>
              </div>
            )}
          </div>
        </Link>

        {/* Crave Button - Inside box at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-2 z-10">
          <CraveButton item={item} weekIndex={weekIndex} votingEnded={votingEnded} />
        </div>
      </div>
      
      {/* Artist Info - Clickable with avatar */}
      <Link 
        to={`/artist/${item.artistId}`}
        className="flex items-center gap-2 px-1 hover:opacity-70 transition-opacity"
      >
        {artist?.avatarUrl && (
          <img
            src={artist.avatarUrl}
            alt={formatArtistDisplayName(artist) || artist.displayName || "Artist"}
            className="w-6 h-6 rounded-full object-cover flex-shrink-0"
          />
        )}
        <span className="text-card-artist truncate">
          {formatArtistDisplayName(artist) || artist?.displayName || "Artist Name"}
        </span>
      </Link>
    </div>
  )
}) 